import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Stack from '@mui/material/Stack';
import React from 'react';
import TimelineItem from '@mui/lab/TimelineItem';
import { Box, Theme, Typography } from '@mui/material';
import TimelineContent from '@mui/lab/TimelineContent';
import EventTitle from './components/EventTitle';
import EventIcon from './components/EventIcon';
import EventContent from './components/EventContent';
import { HISTORY_EVENT } from '../../models/eventTypes';

interface HistoryTimelineItemProps {
  isPending: boolean
  isConnected: boolean
  isDeclined: boolean
  lastEvent: HistoryEventType
  events: TranslationHistoryItem[]
  isDeactivated: boolean
  reactivatedTranslation: string | null
}

const HistoryTimelineItem: React.FC<HistoryTimelineItemProps> = ({ isDeclined, isPending, isConnected, events, isDeactivated, reactivatedTranslation }) => {
  const firstEvent = events[0];
  const secondEvent = events[1];
  const fullEvent = events.length > 1;
  const isDeactivatedOnly = events.length === 1 && (firstEvent.eventType === HISTORY_EVENT.Deactivated || firstEvent.eventType === HISTORY_EVENT.Deactivation);
  const boxClassName = `${fullEvent ? 'full-event' : ''} ${isDeactivatedOnly ? 'deactivated-only' : ''}`.trim();
  const translationLegend = reactivatedTranslation && (
    <Typography variant="body2" sx={styles.reactivatedTranslation}>
      {reactivatedTranslation}
    </Typography>
  );

  return (
    <Box sx={styles} className={boxClassName}>
      <TimelineItem>
        <TimelineSeparator>
          <EventIcon eventType={firstEvent.eventType} isPending={isPending} />
          {isConnected && events.length === 1 && !isDeactivatedOnly && <TimelineConnector />}
        </TimelineSeparator>
        <TimelineContent>
          <Stack>
            <EventTitle title={firstEvent.title} date={firstEvent.date} />
            <EventContent event={firstEvent} />
            {!secondEvent && translationLegend}
          </Stack>
        </TimelineContent>
      </TimelineItem>

      {Boolean(secondEvent) && (
        <TimelineItem>
          <TimelineSeparator>
            <EventIcon eventType={secondEvent.eventType} isPending={isPending} isDeclined={isDeclined} isDeactivated={isDeactivated} />
            {isConnected && <TimelineConnector />}
          </TimelineSeparator>
          <TimelineContent>
            <Stack>
              <EventTitle title={secondEvent.title} date={secondEvent.date} disabled={isDeclined} />
              <EventContent event={secondEvent} disabled={isDeclined} />
              {translationLegend}
            </Stack>
          </TimelineContent>
        </TimelineItem>
      )}
    </Box>
  );
};

const styles = {
  declinedText: {
    color: (theme: Theme) => theme.palette.grey[400]
  },
  mb: 1,
  '&.full-event .MuiTimelineItem-root:first-of-type': {
    minHeight: 1,
    mt: 0,

    '.MuiTimelineDot-root': {
      m: 0,
      mt: 0.3,
    }
  },
  '&.deactivated-only': {
    mb: 0,
  },
  '&.deactivated-only + &': {
    mt: -6,
  },
  '& .MuiTimelineContent-root': {
    p: 0,
    pl: 0.7,
  },
  reactivatedTranslation: {
    fontStyle: 'italic',
    color: 'text.secondary',
  },
} as const

export default HistoryTimelineItem;
